<template>
    <div
        class="md-map layout1 mg-bg"
        v-if="showModule"
        :style="{ ...currentColorStyle, ...spaceStyle }"
    >
        <div class="f-col">
            <site-title v-if="title" :title="title" />
            <div class="map-content">
                <map-tab
                    :showCollapse="showCollapse"
                    v-model:replace="replace"
                    :businessGroups="businessGroups"
                    @updateMarkerEvent="updateMarkerEvent"
                    @updateMarkerData="updateMarkerData"
                    @doMapEvent="doMapEvent"
                    @visible="handleVisible"
                >
                    <div
                        class="map-container"
                        :class="{
                            'has-walk-score': hasWalkScore,
                        }"
                        :style="isMobileSize ? containerStyle : contentStyle"
                    >
                        <div v-if="replace" class="map-replace">
                            <iframe
                                frameborder="0"
                                scrolling="no"
                                marginheight="0"
                                marginwidth="0"
                                :src="`https://maps.google.com/maps?q=${lat},${lng}&z=${
                                    parseInt(zoom) || 14
                                }&output=embed`"
                                :style="{
                                    width: showGMap ? '100%' : '0%',
                                    height: '100%'
                                }"
                                @load="replaceMapLoad = true"
                            ></iframe>
                        </div>
                        <component
                            :is="componentName"
                            v-else-if="canLoadMap && mapShow"
                            :zoom="zoom"
                            :mapConfig="mapConfig"
                            :tools="calTools"
                            :center="center"
                            :address="address"
                            :autoCenter="autoCenter"
                            :mapChangeEvent="mapChangeEvent"
                            :markerData="markerData"
                            :markerEventMap="markerEventMap"
                            @mapLoad="mapLoad"
                            @click="mapClick"
                        />

                        <div
                            v-if="hasWalkScore"
                            class="work-score"
                            :class="{ 'hide-score': hideScore }"
                        >
                            <div class="score-wrap">
                                <span class="score" :class="walkscoreClass">
                                    {{ listingWalkScore.walkscore }} 
                                </span>
                                <span class="label">
                                    {{ $t('md-map.walk') }}{{ $t('md-map.score') }}
                                </span>
                            </div>
                            <span v-if="listingWalkScore.description" class="info">
                                {{ listingWalkScore.description }}
                            </span>
                        </div>
                    </div>
                </map-tab>
            </div>
        </div>
    </div>
</template>

<script>
import { mapState as rootMapState } from 'pinia';
import { usePageStore } from '@/store';
import { defineAsyncComponent } from 'vue';
import common from '../index';
import mapTab from './map-tab.vue';
import { calcColorRootVar } from '@/init/data/handlerData.js';

export default {
    data() {
        return {
            requestAction: { tab: ['getBusinessData'], boundary: true }
        }
    },
    mixins: [common],
    computed: {
        ...rootMapState(usePageStore, {
            'page': store => store.page || {}
        }),
        calTools() {
            if (this.isMobileSize && this.listingWalkScore && this.scoreTools) {
                return this.scoreTools;
            } else {
                return this.tools;
            }
        },
        componentName({ page }) {
            return page.map?.type === 'google' ? 'Map' : 'hereMap';
        },
        businessGroups() {
            return this.tabs.find(tab => tab.type === 'business')?.groups || [];
        },
        currentColorStyle() {
            return {
                ...this.colorStyle,
                ...calcColorRootVar.call(this, {
                    colors: ['--g-primary-color_5', '--color-border_50']
                })
            }
        },
        hasWalkScore() {
            return this.listingWalkScore && this.listingWalkScore.walkscore > 0;
        },
        showModule({ show, lat, lng, isHideSchoolMap, businessGroups }) {
            return show && lat && lng && !isHideSchoolMap && businessGroups.length
        }
    },
    components: {
        Map: defineAsyncComponent(() =>
            import(
                /* webpackChunkName: "map_broker"*/ '@/components/map/layout1.vue'
            )),
        hereMap: defineAsyncComponent(() =>
            import(
                /* webpackChunkName: "map-here_broker"*/ '@/components/map-here/layout1.vue'
            )),
        mapTab
    },
    created() {
        this.selectTab = 'business';
    }
};
</script>

<style lang="scss">
@import '../../../theme/media.scss';
@import '../../../theme/mixin.scss';

.md-map.layout1 {
    @include media-600 {
        .mg-container {
            overflow: visible;
        }
    }
    .site-title {
        padding-bottom: 20px;
    }
    .map-content {
        .map-container {
            height: 350px;
            position: relative;
            .map-replace {
                position: absolute;
                left: 0;
                right: 0;
                top: 0;
                bottom: 0;
            }
            .zoom.standard .zoom-in:before,
            .zoom.standard .zoom-in:after,
            .zoom.standard .zoom-out:before,
            .zoom.standard .zoom-out:after {
                background-color: #191919;
            }
            .site-map.layout1 .marker-icon .marker-pop {
                background-color: #fff;
                color: #191919;
                font-size: 14px;
                font-family: var(--font-normal);
                box-shadow: 0 2px 10px 0 rgba(0, 10, 30, 0.1);
                .pointer {
                    border-top-color: #fff;
                }
            }

            .work-score {
                position: absolute;
                top: 20px;
                left: 20px;
                background-color: #fff;
                padding: 10px;
                color: var(--g-primary-color);
                display: flex;
                align-items: center;
                font-size: 14px;
                .score-wrap {
                    display: flex;
                    align-items: center;
                    height: 30px;
                }
                .score {
                    font-size: 24px;
                    font-weight: 700;
                    margin-right: 10px;
                }
                .info {
                    &:before {
                        content: '';
                        display: inline-block;
                        width: 1px;
                        height: 16px;
                        background-color: var(--color-border);
                        margin: 0 10px;
                        vertical-align: middle;
                    }
                }
            }

        }
        @include media-600 {
            height: auto;
            display: block;
            .map-container {
                width: 100%;
                height: 100vw;
                &.has-walk-score {
                    margin-bottom: 60px;
                }

                .map-replace {
                    top: 58px;
                }

                .work-score {
                    position: static;
                    background-color: var(--color-border_50);
                    padding: 4px 10px;
                    font-size: 14px;
                    .score-wrap {
                        height: 32px;
                    }
                }
            }
        }
    }

    .site-map.layout1 {
        .marker-icon {
            &.business {
                cursor: pointer;
                .iconfont {
                    color: var(--g-primary-color);
                    &:before {
                        font-size: 14px;
                    }
                    &:after {
                        width: 28px;
                        height: 28px;
                    }
                }
                &.active {
                    .iconfont {
                        color: var(--g-btn-background);
                        &:before {
                            color: var(--g-btn-color);
                        }
                    }
                }
            }
        }
    }
}
</style>
